import { QueryClient } from "@tanstack/react-query";

/**
 * Global QueryClient instance with cost-saving and network-friendly defaults
 * tailored for resource-metered deployments (e.g., Railway).
 */

const SIX_HOURS = 1000 * 60 * 60 * 6;
const EIGHT_HOURS = 1000 * 60 * 60 * 8;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: SIX_HOURS, // 6 hours cache freshness
      gcTime: EIGHT_HOURS, // 8 hours garbage collection / cache time
      refetchOnWindowFocus: false, // Prevent aggressive refetches on tab switching
      retry: 1, // Limit retries to reduce load on failing endpoints
    },
  },
});

/**
 * Structured, hierarchical query keys for all server-state domains across all dashboard levels:
 * - Public / Landing
 * - Students
 * - Instructors
 * - Admin
 * - SuperAdmin
 * - Verification
 */
export const queryKeys = {
  // Public & Student Courses / Tracks
  courses: {
    all: ["courses"] as const,
    list: () => [...queryKeys.courses.all, "list"] as const,
    detail: (slugOrId: string | number) =>
      [...queryKeys.courses.all, "detail", String(slugOrId)] as const,
  },
  tracks: {
    all: ["tracks"] as const,
    list: () => [...queryKeys.tracks.all, "list"] as const,
    detail: (slugOrId: string | number) =>
      [...queryKeys.tracks.all, "detail", String(slugOrId)] as const,
  },
  enrollments: {
    all: ["enrollments"] as const,
    list: () => [...queryKeys.enrollments.all, "list"] as const,
    status: (params: { courseId?: string | number; trackId?: string | number }) =>
      [...queryKeys.enrollments.all, "status", params] as const,
    adminLists: () => [...queryKeys.enrollments.all, "adminList"] as const,
    adminList: (params?: Record<string, any>) =>
      params
        ? ([...queryKeys.enrollments.adminLists(), params] as const)
        : ([...queryKeys.enrollments.adminLists()] as const),
    adminDetail: (id: number | string, mode?: string) =>
      [...queryKeys.enrollments.all, "adminDetail", String(id), mode || "online"] as const,
  },
  payments: {
    all: ["payments"] as const,
    history: () => [...queryKeys.payments.all, "history"] as const,
  },
  blog: {
    all: ["blog"] as const,
    list: () => [...queryKeys.blog.all, "list"] as const,
    detail: (slugOrId: string | number) =>
      [...queryKeys.blog.all, "detail", String(slugOrId)] as const,
    categories: () => [...queryKeys.blog.all, "categories"] as const,
    tags: () => [...queryKeys.blog.all, "tags"] as const,
  },
  services: {
    all: ["services"] as const,
    list: () => [...queryKeys.services.all, "list"] as const,
  },
  jobs: {
    all: ["jobs"] as const,
    list: () => [...queryKeys.jobs.all, "list"] as const,
  },
  events: {
    all: ["events"] as const,
    list: () => [...queryKeys.events.all, "list"] as const,
  },
  companyInfo: () => ["companyInfo"] as const,
  landingBundle: () => ["landingBundle"] as const,

  // Curriculum Management (Instructors & SuperAdmin)
  curriculumAdmin: {
    all: ["curriculumAdmin"] as const,
    coursesList: () => [...queryKeys.curriculumAdmin.all, "courses"] as const,
    courseDetail: (id: string | number) =>
      [...queryKeys.curriculumAdmin.all, "course", String(id)] as const,
    tracksList: () => [...queryKeys.curriculumAdmin.all, "tracks"] as const,
    trackDetail: (id: string | number) =>
      [...queryKeys.curriculumAdmin.all, "track", String(id)] as const,
    presences: () => [...queryKeys.curriculumAdmin.all, "presences"] as const,
    instructorStudents: (scope?: string) =>
      [...queryKeys.curriculumAdmin.all, "instructorStudents", scope || "default"] as const,
  },

  // Company Price List & Analytics (Admin & SuperAdmin)
  priceList: {
    all: ["priceList"] as const,
    list: (category?: string, query?: string) =>
      [...queryKeys.priceList.all, "list", { category, query }] as const,
    overview: () => [...queryKeys.priceList.all, "overview"] as const,
  },

  // Visitors (Admin & SuperAdmin)
  visitors: {
    all: ["visitors"] as const,
    list: (params?: any) =>
      params
        ? ([...queryKeys.visitors.all, "list", params] as const)
        : ([...queryKeys.visitors.all, "list"] as const),
  },

  // Wholesale Agents
  agents: {
    all: ["agents"] as const,
    list: () => [...queryKeys.agents.all, "list"] as const,
  },

  // Service Rates
  serviceRates: {
    all: ["serviceRates"] as const,
    list: (showAll?: boolean) =>
      [...queryKeys.serviceRates.all, "list", { showAll: Boolean(showAll) }] as const,
  },

  // Staff Jobs & Monthly Ledger (Admin & SuperAdmin)
  staffJobs: {
    all: ["staffJobs"] as const,
    lists: () => [...queryKeys.staffJobs.all, "list"] as const,
    list: (params?: any) =>
      params
        ? ([...queryKeys.staffJobs.lists(), params] as const)
        : ([...queryKeys.staffJobs.lists()] as const),
    summary: () => [...queryKeys.staffJobs.all, "summary"] as const,
    agents: () => queryKeys.agents.list(),
    serviceRates: (showAll?: boolean) => queryKeys.serviceRates.list(showAll),
    payoutSummary: (month?: string) =>
      [...queryKeys.staffJobs.all, "payoutSummary", month || "current"] as const,
  },
  monthlyLedger: {
    all: ["monthlyLedger"] as const,
    list: (params?: any) => [...queryKeys.monthlyLedger.all, "list", params] as const,
  },

  // Finance & Payments (Admin & SuperAdmin)
  finance: {
    all: ["finance"] as const,
    summary: () => [...queryKeys.finance.all, "summary"] as const,
    transactions: (page?: number) =>
      [...queryKeys.finance.all, "transactions", page ?? 1] as const,
  },

  // Verification Dashboard
  verification: {
    all: ["verification"] as const,
    wallet: () => [...queryKeys.verification.all, "wallet"] as const,
    staffWallets: () => [...queryKeys.verification.all, "staffWallets"] as const,
    services: () => [...queryKeys.verification.all, "services"] as const,
    logs: () => [...queryKeys.verification.all, "logs"] as const,
  },

  // Media Library
  media: {
    all: ["media"] as const,
    list: (params?: { media_type?: string; search?: string }) =>
      params
        ? ([...queryKeys.media.all, "list", params] as const)
        : ([...queryKeys.media.all, "list"] as const),
  },

  // User Management Directory (SuperAdmin)
  users: {
    all: ["users"] as const,
    lists: () => [...queryKeys.users.all, "list"] as const,
    list: (params?: { search?: string; role?: string; status?: string }) =>
      params
        ? ([...queryKeys.users.lists(), params] as const)
        : ([...queryKeys.users.lists()] as const),
    detail: (id: number | string) => [...queryKeys.users.all, "detail", String(id)] as const,
  },

  // Role-Based Access Control & Governance Matrix (SuperAdmin)
  rbac: {
    all: ["rbac"] as const,
    matrix: () => [...queryKeys.rbac.all, "matrix"] as const,
  },

  // GitHub Assignments & Autograder Submissions
  assignments: {
    all: ["assignments"] as const,
    submissions: () => [...queryKeys.assignments.all, "submissions"] as const,
    courseSubmission: (courseId: number | string) =>
      [...queryKeys.assignments.all, "course", String(courseId)] as const,
    exerciseSubmission: (exerciseId: number | string) =>
      [...queryKeys.assignments.all, "exercise", String(exerciseId)] as const,
    codespaceStatus: (submissionId: number | string) =>
      [...queryKeys.assignments.all, "codespaceStatus", String(submissionId)] as const,
  },

  // Social Media Autoposter (Admin & SuperAdmin)
  socialBroadcast: {
    all: ["socialBroadcast"] as const,
    broadcasts: () => [...queryKeys.socialBroadcast.all, "broadcasts"] as const,
    broadcastDetail: (id: number | string) =>
      [...queryKeys.socialBroadcast.all, "broadcast", String(id)] as const,
    configs: () => [...queryKeys.socialBroadcast.all, "configs"] as const,
    configDetail: (id: number | string) =>
      [...queryKeys.socialBroadcast.all, "config", String(id)] as const,
    logs: (params?: { platform?: string; status?: string; broadcast_id?: number | string }) =>
      params
        ? ([...queryKeys.socialBroadcast.all, "logs", params] as const)
        : ([...queryKeys.socialBroadcast.all, "logs"] as const),
  },
};
